import React from 'react';
import { useTranslation } from 'react-i18next';
import type { RosterViewMode } from '../types/Rostering';
import {
  addDays,
  addMonths,
  buildMonthCalendarDays,
  startOfMonth,
  startOfWeekMonday,
} from '../utils/rosterUtils';
import {
  formatCompactWeekRangeLocalized,
  formatMonthYearLabel,
  getMondayWeekdayLabelsLocalized,
  MONDAY_FIRST_WEEKDAY_KEYS,
} from '../utils/rosterDateLabels';
import styles from './WeekRangePicker.module.css';

export type WeekRangePickerProps = {
  mode?: RosterViewMode;
  weekStart: Date;
  monthStart: Date;
  onWeekChange: (weekStart: Date) => void;
  onMonthChange: (monthStart: Date) => void;
  className?: string;
};

export const WeekRangePicker: React.FC<WeekRangePickerProps> = ({
  mode = 'week',
  weekStart,
  monthStart,
  onWeekChange,
  onMonthChange,
  className,
}) => {
  const { t } = useTranslation('rostering');
  const pickerRef = React.useRef<HTMLDivElement | null>(null);
  const [isOpen, setIsOpen] = React.useState(false);
  const anchorDate = mode === 'month' ? monthStart : weekStart;
  const [visibleMonth, setVisibleMonth] = React.useState(() => startOfMonth(anchorDate));

  const weekEnd = React.useMemo(() => addDays(weekStart, 6), [weekStart]);
  const rangeLabel = React.useMemo(
    () => (
      mode === 'month'
        ? formatMonthYearLabel(monthStart, t)
        : formatCompactWeekRangeLocalized(weekStart, weekEnd, t)
    ),
    [mode, monthStart, t, weekEnd, weekStart],
  );
  const weekdayLabels = React.useMemo(
    () => getMondayWeekdayLabelsLocalized(t),
    [t],
  );
  const calendarDays = React.useMemo(
    () => buildMonthCalendarDays(
      visibleMonth,
      mode === 'month'
        ? { type: 'month', monthStart }
        : { type: 'week', weekStart },
    ),
    [mode, monthStart, visibleMonth, weekStart],
  );
  const monthLabel = React.useMemo(
    () => formatMonthYearLabel(visibleMonth, t),
    [t, visibleMonth],
  );

  React.useEffect(() => {
    if (isOpen) {
      setVisibleMonth(startOfMonth(anchorDate));
    }
  }, [anchorDate, isOpen]);

  React.useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (!pickerRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [isOpen]);

  const goToToday = () => {
    if (mode === 'month') {
      onMonthChange(startOfMonth(new Date()));
      return;
    }

    onWeekChange(startOfWeekMonday(new Date()));
  };

  const goPrevious = () => {
    if (mode === 'month') {
      onMonthChange(startOfMonth(addMonths(monthStart, -1)));
      return;
    }

    onWeekChange(addDays(weekStart, -7));
  };

  const goNext = () => {
    if (mode === 'month') {
      onMonthChange(startOfMonth(addMonths(monthStart, 1)));
      return;
    }

    onWeekChange(addDays(weekStart, 7));
  };

  const selectCalendarDay = (date: Date) => {
    if (mode === 'month') {
      onMonthChange(startOfMonth(date));
    } else {
      onWeekChange(startOfWeekMonday(date));
    }

    setIsOpen(false);
  };

  return (
    <div className={`${styles.picker} ${className ?? ''}`.trim()} ref={pickerRef}>
      <button
        type="button"
        className="btn btn-secondary"
        onClick={goToToday}
      >
        {t('today')}
      </button>

      <div className={styles.rangeGroup}>
        <button
          type="button"
          className={styles.navButton}
          onClick={goPrevious}
          aria-label={mode === 'month' ? t('previousMonth') : t('previousWeek')}
        >
          ‹
        </button>

        <button
          type="button"
          className={`${styles.rangeButton} ${isOpen ? styles.rangeButtonOpen : ''}`.trim()}
          onClick={() => setIsOpen((current) => !current)}
          aria-expanded={isOpen}
          aria-haspopup="dialog"
        >
          {rangeLabel}
        </button>

        <button
          type="button"
          className={styles.navButton}
          onClick={goNext}
          aria-label={mode === 'month' ? t('nextMonth') : t('nextWeek')}
        >
          ›
        </button>
      </div>

      {isOpen ? (
        <div className={styles.popover} role="dialog" aria-label={rangeLabel}>
          <div className={styles.monthHeader}>
            <button
              type="button"
              className={styles.monthNavButton}
              onClick={() => setVisibleMonth((current) => addMonths(current, -1))}
              aria-label={t('previousMonth')}
            >
              ‹
            </button>
            <span className={styles.monthLabel}>{monthLabel}</span>
            <button
              type="button"
              className={styles.monthNavButton}
              onClick={() => setVisibleMonth((current) => addMonths(current, 1))}
              aria-label={t('nextMonth')}
            >
              ›
            </button>
          </div>

          <div className={styles.weekdayRow}>
            {weekdayLabels.map((label, index) => (
              <span key={MONDAY_FIRST_WEEKDAY_KEYS[index]} className={styles.weekdayLabel}>{label}</span>
            ))}
          </div>

          <div className={styles.dayGrid}>
            {calendarDays.map((day) => (
              <button
                key={day.dayKey}
                type="button"
                className={[
                  styles.dayButton,
                  !day.isCurrentMonth ? styles.dayOutsideMonth : '',
                  mode === 'month' && day.isInSelectedMonth ? styles.dayInMonth : '',
                  mode === 'week' && day.isInSelectedWeek ? styles.dayInWeek : '',
                  mode === 'week' && day.isWeekStart ? styles.dayWeekStart : '',
                  mode === 'week' && day.isWeekEnd ? styles.dayWeekEnd : '',
                  mode === 'week' && day.isWeekStart ? styles.daySelectedStart : '',
                  mode === 'month' && day.isInSelectedMonth && day.isCurrentMonth && day.date.getDate() === 1
                    ? styles.daySelectedStart
                    : '',
                  day.isToday ? styles.dayToday : '',
                ].filter(Boolean).join(' ')}
                onClick={() => selectCalendarDay(day.date)}
              >
                {day.dayNumber}
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
};
